import { useState, useRef } from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import chroma from 'chroma-js';
import { RADAR_LEGEND_COLORS } from 'libs/terrier/useTerrierRadar/radarColorMap';
import { TEXT_LABELS, RATE_LABELS, DBZ_LABELS } from './RadarLegend.constants';
import type { LegendItem, LegendMode } from './RadarLegend.types';

// Flatten colors by reducing luminance contrast and desaturating
const mute = (hex: string): string => {
  const c = chroma(hex);
  const [h, s, l] = c.hsl();
  // Compress lightness toward middle (0.5) and reduce saturation
  const newL = 0.5 + (l - 0.5) * 0.6;
  const newS = s * 0.7;
  return chroma.hsl(h, newS, newL).hex();
};

// Gradient colors in order (5-65 dBZ in 10 dBZ increments)
const GRADIENT_COLORS = [
  RADAR_LEGEND_COLORS.LIGHT_BLUE,
  RADAR_LEGEND_COLORS.BLUE,
  RADAR_LEGEND_COLORS.GREEN,
  RADAR_LEGEND_COLORS.YELLOW,
  RADAR_LEGEND_COLORS.ORANGE,
  RADAR_LEGEND_COLORS.DARK_RED,
  RADAR_LEGEND_COLORS.MAGENTA,
];

const MUTED_COLORS = GRADIENT_COLORS.map(mute);

// Darken the first color for better visibility
const DARKENED_FIRST = chroma(MUTED_COLORS[0]).darken(1).hex();

// Gradient stops: 5-65 dBZ in 10 dBZ increments (7 stops)
const LEGEND_GRADIENT = `linear-gradient(to right, ${DARKENED_FIRST} 0%, ${MUTED_COLORS[1]} 16.7%, ${MUTED_COLORS[2]} 33.3%, ${MUTED_COLORS[3]} 50%, ${MUTED_COLORS[4]} 66.7%, ${MUTED_COLORS[5]} 83.3%, ${MUTED_COLORS[6]} 100%)`;

const textSx = {
  flex: '1 1 0',
  minWidth: 0,
  textAlign: 'center',
  // Always light text on the dark radar gradient (do not use common.white — inverted in brand theme).
  color: '#FFFFFF !important',
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textShadow: '0px 0px 4px rgba(0, 0, 0, 0.6)',
} as const;

const LEGEND_ITEMS: Record<LegendMode, LegendItem[]> = {
  labels: TEXT_LABELS,
  rate: RATE_LABELS,
  dbz: DBZ_LABELS,
};

// Minimum px movement to consider a swipe (not a tap)
const SWIPE_THRESHOLD = 10;

export const RadarLegend = (): JSX.Element => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<LegendMode>('labels');
  const touchStartX = useRef<number | null>(null);

  const cycleMode = (): void =>
    setMode((m) => {
      if (m === 'labels') return 'rate';
      if (m === 'rate') return 'dbz';
      return 'labels';
    });

  const handleTouchStart = (e: React.TouchEvent): void => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent): void => {
    if (touchStartX.current === null) return;

    const deltaX = Math.abs(e.changedTouches[0].clientX - touchStartX.current);
    const wasTap = deltaX < SWIPE_THRESHOLD;

    touchStartX.current = null;

    if (wasTap) {
      e.preventDefault(); // Prevent subsequent click event
      cycleMode();
    }
  };

  // Desktop only - touch devices use handleTouchEnd
  const handleClick = (): void => {
    cycleMode();
  };

  return (
    <Stack
      direction="row"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        backgroundColor: 'grey.900',
        backgroundImage: LEGEND_GRADIENT,
        cursor: 'pointer',
        height: '100%',
        alignItems: 'center',
      }}
    >
      {LEGEND_ITEMS[mode].map(({ key, getLabel }) => (
        <Typography key={key} sx={{ fontSize: 12, ...textSx }}>
          {getLabel(t)}
        </Typography>
      ))}
    </Stack>
  );
};
